"use client";

import { useState, useCallback } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Textarea, Switch, Card, CardBody } from "@nextui-org/react";
import { Upload, X, Flame, Beef, Citrus, Egg, Heart, Leaf, Droplet, HelpCircle, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { BasicUserType } from "@/types/user";
import { apiClient } from "@/lib/api-client";

interface SimplifiedMealModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: BasicUserType | null;
}

export function SimplifiedMealModal({ isOpen, onClose, user }: SimplifiedMealModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [image, setImage] = useState<string>();
  const [overlayImage, setOverlayImage] = useState<string>();
  const [postCaption, setPostCaption] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [isOrganic, setIsOrganic] = useState(false);
  const [isSeedOilFree, setIsSeedOilFree] = useState(false);
  const [analysis, setAnalysis] = useState<any>(null);
  
  // Reset all state
  const resetState = () => {
    setImage(undefined);
    setOverlayImage(undefined);
    setPostCaption("");
    setIsDragging(false);
    setIsOrganic(false);
    setIsSeedOilFree(false);
    setAnalysis(null);
  };
  
  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e?.dataTransfer?.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, []);
  
  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };
  
  // Process the file
  const handleFile = (file: File) => {
    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setImage(result);
      // Automatically analyze the image
      analyzeImage(result);
    };
    reader.onerror = () => {
      console.error("FileReader error:", reader.error);
      toast.error("Failed to read image file");
    };
    reader.readAsDataURL(file);
  };
  
  // Analyze the image
  const analyzeImage = async (imageData: string) => {
    if (!imageData) {
      toast.error("No image to analyze");
      return;
    }

    try {
      setIsAnalyzing(true);
      toast.loading("Analyzing your meal...");
      
      const response = await apiClient.analyzeMeal({
        image: imageData,
        createPostWithResults: false
      });

      const analysisData = response.analysis;
      
      // Log the analysis data to understand its structure
      console.log("Analysis data structure:", JSON.stringify({
        nutritional_analysis: analysisData.nutritional_analysis,
        dietary_notes: analysisData.dietary_notes,
        ingredients: analysisData.ingredients
      }, null, 2));
      
      setAnalysis(analysisData);
      
      // Generate a preview caption for the post
      const foodItems = analysisData?.food_detection?.foods_on_plate || [analysisData?.food_detection?.main_dish];
      const foodText = foodItems.join(', ');
      const qualityScore = analysisData?.dietary_notes?.quality_score || analysisData?.dietary_notes?.health_rating || "N/A";
      
      const caption = `I just analyzed my meal: ${foodText}!\n\nCalories: ${analysisData?.nutritional_analysis?.estimated_calories}\nProtein: ${analysisData?.nutritional_analysis?.macronutrients.protein}\nCarbs: ${analysisData?.nutritional_analysis?.macronutrients.carbohydrates.total}\nFats: ${analysisData?.nutritional_analysis?.macronutrients.fats.total}\nQuality Score: ${qualityScore}`;
      setPostCaption(caption);

      // Get the overlay image
      const overlayResponse = await apiClient.getMealOverlay({
        image: imageData,
        analysis: analysisData,
        isOrganic,
        isSeedOilFree
      });

      setOverlayImage(overlayResponse?.overlay?.overlayImageUrl);
      
      toast.dismiss();
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : "Failed to analyze image");
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Post to feed
  const postToFeed = async () => {
    if (!overlayImage) {
      toast.error("No analyzed image to post");
      return;
    }
    
    // Caption is now optional
    
    try {
      setIsLoading(true);
      toast.loading("Posting to feed...");
      
      // IMPORTANT: Use the EXACT raw values from the analysis without any processing or defaults
      const calories = analysis?.nutritional_analysis?.estimated_calories;
      const protein = analysis?.nutritional_analysis?.macronutrients?.protein;
      const carbs = analysis?.nutritional_analysis?.macronutrients?.carbohydrates?.total;
      const fats = analysis?.nutritional_analysis?.macronutrients?.fats?.total;
      const qualityScore = analysis?.dietary_notes?.quality_score || analysis?.dietary_notes?.health_rating;
      
      // Log the raw values from the analysis
      console.log("EXACT raw nutritional values from analysis:", {
        calories,
        protein,
        carbs,
        fats,
        qualityScore
      });
      
      // Create nutritional info object with raw values
      const nutritionalInfo = analysis ? {
        calories,
        protein,
        carbs,
        fats,
        qualityScore
      } : undefined;

      // Log the nutritional info to verify it's being extracted correctly
      console.log("Extracted nutritional info:", nutritionalInfo);
      
      // Log the raw values from analysis to verify they exist
      if (analysis) {
        console.log("Raw nutritional values from analysis:", {
          calories: analysis?.nutritional_analysis?.estimated_calories,
          protein: analysis?.nutritional_analysis?.macronutrients.protein,
          carbs: analysis?.nutritional_analysis?.macronutrients.carbohydrates.total,
          fats: analysis?.nutritional_analysis?.macronutrients.fats.total
        });
      }

      // Get meal name for default content
      const mealName = analysis ? (analysis?.food_detection?.foods_on_plate?.join(', ') || analysis?.food_detection?.main_dish) : "Meal";
      
      // Create post data with default caption if empty
      const defaultCaption = `I just analyzed my ${mealName}!\n\nCalories: ${calories}\nProtein: ${protein}\nCarbs: ${carbs}\nFats: ${fats}`;
      
      const postData = {
        content: postCaption.trim() || defaultCaption, // Use default caption if empty
        type: "meal",
        isOrganic,
        isSeedOilFree,
        meal: {
          name: mealName,
          description: "",
          ingredients: analysis?.ingredients?.main_ingredients || [],
          brands: [],
          isOrganic,
          hasSeedOils: !isSeedOilFree,
          // Include both the nutritional info object and direct values
          nutritionalInfo,
          // Direct values for easier access
          calories,
          protein,
          carbs,
          fats,
          qualityScore
        },
        medias: [
          {
            url: overlayImage,
            type: "image"
          }
        ]
      };

      // Log the post data to verify it's being created correctly
      console.log("Post data being sent:", JSON.stringify(postData, null, 2));

      // Create a post with the overlay image and caption
      const response = await apiClient.createPost(postData as any);
      
      const createdPost = response.post;
      
      // Log the created post to verify what's being returned from the API
      console.log("Created post returned from API:", JSON.stringify(createdPost, null, 2));
      
      toast.dismiss();
      toast.success("Meal posted successfully to your feed!");
      
      // Update the feed immediately without reloading
      if (window.updateFeed && typeof window.updateFeed === 'function') {
        window.updateFeed(createdPost as any);
      }
      
      // Reset the state to clear the modal
      resetState();
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error("Error posting meal:", error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : "Failed to post meal");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Log privately (placeholder for now)
  const logPrivately = () => {
    toast.info("Log privately feature coming soon");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      className="bg-secondary max-h-[90vh]"
      scrollBehavior="inside"
      closeButton
    >
      <ModalContent>
        <ModalHeader className="flex justify-between items-center border-b sticky top-0 z-10">
          <h2 className="text-xl">Quick Meal Post</h2>
          <div className="flex gap-2 mr-12">
            <Button
              variant="light"
              onPress={onClose}
              isDisabled={isLoading || isAnalyzing}
            >
              Cancel
            </Button>
          </div>
        </ModalHeader>
        <ModalBody className="overflow-y-auto py-6">
          <div className="space-y-6">
            {!image ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging ? "border-primary bg-primary/10" : "border-gray-300"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center gap-4">
                  <Upload className="h-12 w-12 text-gray-400" />
                  <h3 className="text-lg font-medium">
                    {isDragging ? "Drop your image here" : "Drag & drop your meal photo here"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    or
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="simplified-image-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="simplified-image-upload"
                    color="primary"
                    className="cursor-pointer"
                  >
                    Browse Files
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="relative">
                    <img
                      src={image}
                      alt="Meal"
                      className="w-full rounded-lg shadow-lg"
                    />
                    <Button
                      isIconOnly
                      color="danger"
                      variant="light"
                      className="absolute top-2 right-2"
                      onPress={resetState}
                    >
                      <X />
                    </Button>
                    {isAnalyzing && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                        <div className="text-white text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
                          <p>Analyzing your meal...</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {!isAnalyzing && analysis && (
                    <div className="grid grid-cols-4 gap-2">
                      {/* Calories */}
                      <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
                        <CardBody className="p-2 flex flex-col items-center">
                          <Flame className="h-6 w-6 text-orange-500 dark:text-orange-400" />
                          <div className="text-sm font-bold">
                            {analysis?.nutritional_analysis?.estimated_calories?.replace(/[^0-9]/g, '') || "N/A"}
                          </div>
                          <div className="text-xs text-default-500">Calories</div>
                        </CardBody>
                      </Card>
                      
                      {/* Protein */}
                      <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
                        <CardBody className="p-2 flex flex-col items-center">
                          <Beef className="h-6 w-6 text-red-500 dark:text-red-400" />
                          <div className="text-sm font-bold">{analysis?.nutritional_analysis?.macronutrients.protein.replace(/[^0-9]/g, '')}</div>
                          <div className="text-xs text-default-500">Protein</div>
                        </CardBody>
                      </Card>
                      
                      {/* Carbs */}
                      <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
                        <CardBody className="p-2 flex flex-col items-center">
                          <Citrus className="h-6 w-6 text-blue-500 dark:text-blue-400" />
                          <div className="text-sm font-bold">{analysis?.nutritional_analysis?.macronutrients.carbohydrates?.total?.replace(/[^0-9]/g, '')}</div>
                          <div className="text-xs text-default-500">Carbs</div>
                        </CardBody>
                      </Card>
                      
                      {/* Fats */}
                      <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
                        <CardBody className="p-2 flex flex-col items-center">
                          <Egg className="h-6 w-6 text-yellow-500 dark:text-yellow-400" />
                          <div className="text-sm font-bold">{analysis?.nutritional_analysis?.macronutrients.fats?.total?.replace(/[^0-9]/g, '')}</div>
                          <div className="text-xs text-default-500">Fats</div>
                        </CardBody>
                      </Card>
                    </div>
                  )}
                  
                  {!isAnalyzing && analysis && (
                    <div className="grid grid-cols-3 gap-2">
                      {/* Quality Score */}
                      <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
                        <CardBody className="p-2 flex flex-col items-center">
                          <Heart className="h-6 w-6 text-pink-500 dark:text-pink-400" />
                          <div className="text-sm font-bold">
                            {(analysis?.dietary_notes?.quality_score || analysis?.dietary_notes?.health_rating || "?").toString().replace(/[^0-9]/g, '')}/100
                          </div>
                          <div className="text-xs text-default-500">Quality</div>
                        </CardBody>
                      </Card>
                      
                      {/* Organic */}
                      <Card
                        className={`bg-content2 dark:bg-content2 shadow-sm border border-divider cursor-pointer ${isOrganic ? 'border-2 border-green-500 dark:border-green-400' : ''}`}
                        isPressable
                        onPress={() => setIsOrganic(!isOrganic)}
                      >
                        <CardBody className="p-2 flex flex-col items-center">
                          {isOrganic ? (
                            <Leaf className="h-6 w-6 text-green-500 dark:text-green-400" />
                          ) : (
                            <HelpCircle className="h-6 w-6 text-default-400 dark:text-default-500" />
                          )}
                          <div className="text-sm font-bold">{isOrganic ? "Yes" : "?"}</div>
                          <div className="text-xs text-default-500">Organic</div>
                        </CardBody>
                      </Card>
                      
                      {/* Seed Oil Free */}
                      <Card
                        className={`bg-content2 dark:bg-content2 shadow-sm border border-divider cursor-pointer ${isSeedOilFree ? 'border-2 border-green-500 dark:border-green-400' : ''}`}
                        isPressable
                        onPress={() => setIsSeedOilFree(!isSeedOilFree)}
                      >
                        <CardBody className="p-2 flex flex-col items-center">
                          {isSeedOilFree ? (
                            <Droplet className="h-6 w-6 text-green-500 dark:text-green-400" />
                          ) : (
                            <HelpCircle className="h-6 w-6 text-default-400 dark:text-default-500" />
                          )}
                          <div className="text-sm font-bold">{isSeedOilFree ? "Yes" : "?"}</div>
                          <div className="text-xs text-default-500">Seed Oil Free</div>
                        </CardBody>
                      </Card>
                    </div>
                  )}
                </div>
                
                <Textarea
                  label="Caption"
                  value={postCaption}
                  onChange={(e) => setPostCaption(e?.target?.value)}
                  variant="bordered"
                  minRows={3}
                  placeholder="Add a caption to your meal post..."
                />
                
                <div className="flex flex-col gap-4">
                  <div className="flex justify-end gap-2">
                    <Button
                      color="secondary"
                      onPress={() => analyzeImage(image || "")}
                      isDisabled={!image || isAnalyzing}
                      startContent={<Wand2 className="h-4 w-4" />}
                    >
                      {isAnalyzing ? "Analyzing..." : "Analyze Meal"}
                    </Button>
                  <Button
                    variant="light"
                    onPress={logPrivately}
                    isDisabled={isLoading || isAnalyzing || !overlayImage}
                  >
                    Log Privately
                  </Button>
                  <Button
                    color="primary"
                    onPress={postToFeed}
                    isDisabled={isLoading || isAnalyzing || !overlayImage}
                  >
                    {isLoading ? "Posting..." : "Post to Feed"}
                  </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default SimplifiedMealModal;